import { CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/common';
import type { OnboardingProgress as OnboardingProgressType } from '@/api/onboarding';

export interface OnboardingProgressProps {
  progress: OnboardingProgressType | undefined;
  isLoading: boolean;
  onStart: () => void;
}

export function OnboardingProgress({ progress, isLoading, onStart }: OnboardingProgressProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  if (!progress) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">No onboarding data available.</p>
        </CardContent>
      </Card>
    );
  }

  const { total, answered, percent, by_category } = progress;
  const remaining = total - answered;
  const categories = Object.entries(by_category).sort(
    ([, a], [, b]) => b.total - a.total
  );

  return (
    <div className="space-y-6">
      {/* Overall Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {percent >= 100 ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <Clock className="h-5 w-5 text-blue-500" />
            )}
            Onboarding Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress bar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                {answered} of {total} questions answered
              </span>
              <span className="text-sm font-medium text-blue-600">
                {percent}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(percent, 100)}%` }}
              />
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{answered}</p>
              <p className="text-xs text-muted-foreground">Answered</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-500">{remaining}</p>
              <p className="text-xs text-muted-foreground">Remaining</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{total}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
          </div>

          {/* Action button */}
          <Button onClick={onStart} className="w-full" size="lg">
            {answered === 0 ? 'Start Onboarding' : 'Continue Onboarding'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>

      {/* Per-category breakdown */}
      {categories.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Progress by Category</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {categories.map(([category, data]) => {
              const catPercent =
                data.total > 0
                  ? Math.round((data.answered / data.total) * 100)
                  : 0;

              return (
                <div key={category}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{category}</span>
                    <span className="text-xs text-muted-foreground">
                      {data.answered}/{data.total} ({catPercent}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        catPercent >= 100
                          ? 'bg-green-500'
                          : catPercent > 50
                            ? 'bg-blue-500'
                            : 'bg-orange-400'
                      }`}
                      style={{ width: `${Math.min(catPercent, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
